import { NavBar } from "components/general/NavBar";
import { Footer } from "components/general/Footer";
import { Row, Col, Container, Button, Spinner } from 'react-bootstrap';
import { createContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
// import { useSwipeable } from 'react-swipeable';
// import { useSelector } from "react-redux";

import MeetingCard from "components/Meeting/MeetingCard";
import AssignmentsCard from "components/Meeting/AdminCards/AssignmentsCard";
import PollCard from "components/Meeting/AdminCards/PollsCard";
import TimerCard from "components/Meeting/AdminCards/TimerCard";
import EventsCard from "components/Meeting/AdminCards/EventsCard";
import RolesCard from "components/Meeting/AdminCards/RolesCard";
import AgendaComponent from "components/Meeting/AgendaComponent";


export const MeetingContext = createContext();

export default function Meeting() {
  const params = useParams();
  const url = process.env.REACT_APP_URL;
  const meetingId = params.meetingId;

  const [meeting, setMeeting] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPrevMeeting, setHasPrevMeeting] = useState(false);
  const [hasNextMeeting, setHasNextMeeting] = useState(false);
  const [prevMeetingId, setPrevMeetingId] = useState('');
  const [nextMeetingId, setNextMeetingId] = useState('');
  const [agenda, setAgenda] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [isEditorVisible, setIsEditorVisible] = useState(false);

  const editorRef = useRef(null);

  const toggleEditor = () => setIsEditorVisible((prev) => !prev);

  const getMeetings = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetings`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const meetingsData = await response.json();
        const currentMeetingIndex = meetingsData.findIndex(meeting => meeting._id === meetingId);

        if (currentMeetingIndex !== -1) {
          setHasPrevMeeting(currentMeetingIndex > 0);
          setHasNextMeeting(currentMeetingIndex < meetingsData.length - 1);

          setPrevMeetingId(currentMeetingIndex > 0 ? meetingsData[currentMeetingIndex - 1]._id : '');
          setNextMeetingId(currentMeetingIndex < meetingsData.length - 1 ? meetingsData[currentMeetingIndex + 1]._id : '');
        } else {
          toast.error(`The current meeting was not found in the list of meetings.`, { position: 'bottom-left' });
        }
      } else {
        toast.error(`Unable to load the list of meetings.`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading meetings: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId]);

  const getMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meeting/${meetingId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setMeeting(data);
        setAgenda(data.items);
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load the meeting. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId]);

  const getRoleAssignment = useCallback(async () => {
    try {
      const response = await fetch(`${url}/roleAssignments/${meetingId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const roleAssignment = await response.json();
        setAssignments(roleAssignment);
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load role assignments. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading role assignments: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId]);

  // Callback to reload assignments after adding or removing an assignment
  // const handleAssignmentChange = async () => {
  //   await getRoleAssignment();
  //   await getMeeting();
  // };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getMeetings(), getMeeting(), getRoleAssignment()]);
      setIsLoading(false);
    };

    fetchData();
  }, [getMeetings, getMeeting, getRoleAssignment]);

  const handleClickOutside = (event) => {
    if (editorRef.current && !editorRef.current.contains(event.target)) {
      setIsEditorVisible(false);
    }
  };

  useEffect(() => {
    if (isEditorVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditorVisible]);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <section className="meeting">
        <Container>
          <Row className="mt-3 mb-3">
            <Col>
              {hasPrevMeeting && (
                <Button variant="success" href={`/meeting/${prevMeetingId}`}>
                  <i className="bi bi-chevron-left"></i> Previous Meeting
                </Button>
              )}
            </Col>
            <Col className="text-end">
              {hasNextMeeting && (
                <Button variant="success" href={`/meeting/${nextMeetingId}`}>
                  Next Meeting <i className="bi bi-chevron-right"></i>
                </Button>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <MeetingCard
                meeting={meeting}
                getMeeting={getMeeting}
                assignments={assignments}
                getRoleAssignment={getRoleAssignment}
              // onAssignmentChange={handleAssignmentChange} // Pass callback to MeetingCard
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <AgendaComponent getMeeting={getMeeting} meeting={meeting} />
            </Col>
          </Row>
        </Container>
      </section>
      <Footer />
      {/* {authUser?.user.acl === "Officer" && ( */}
      <button
        onClick={toggleEditor}
        style={{
          position: "fixed",
          top: "100px",
          right: isEditorVisible
            ? window.innerWidth >= 992
              ? "calc(33.333% - 8px)"
              : "calc(83.333% - 8px)"
            : "-8px",
          zIndex: 1050,
          backgroundColor: "black",
          color: "white",
          border: "none",
          padding: "10px",
          borderRadius: "5px",
          cursor: "pointer",
        }}
        className="toggle-editor-btn"
      >
        {isEditorVisible ? (
          <i className="bi bi-chevron-right"></i>
        ) : (
          <i className="bi bi-chevron-left"></i>
        )}
      </button>
      {/* )} */}
      {isEditorVisible && (
        <Col
          id="editor"
          xs={10}
          lg={4}
          ref={editorRef}
          className="editor-overlay"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
            overflow: "auto",
            zIndex: 1040,
            transition: "width 0.3s ease-in-out",
          }}
        >
          <h4 className="p-3">Meeting Tools</h4>
          <Container>
            <AssignmentsCard getMeeting={getMeeting} getRoleAssignment={getRoleAssignment} assignments={assignments} />
            <PollCard agenda={agenda} />
            <TimerCard agenda={agenda} />
            <EventsCard />
            <RolesCard />
          </Container>
        </Col>
      )}
    </>
  );
}